import { gql, TypedDocumentNode } from '@common/hooks';
import { ApprovalChainStatusFragment } from '@approvals/graphql/fragments';
import {
  HistoryInfoFragment,
  HistoryInfoFragmentType,
  RoleLevelEnum,
  ShiftType,
} from '@common/graphql';
import { CommentInfoFragmentType } from '@common/graphql/fragments/comment';
import { BillingCardFragment, BillingCardType } from '@institutions/graphql';

type OrganizationBusinessType = {
  id: string;
  name: string;
};

type ClinicalUnitType = {
  id: string;
  displayName: string;
};

type DepartmentType = {
  id: string;
  organizationBusiness: OrganizationBusinessType;
};

type OrganizationUnitType = {
  id: string;
  name?: string;
  costCenter?: string;
  clinicalUnit?: ClinicalUnitType;
  department?: DepartmentType;
};

type HiringLeaderType = {
  id: string;
  name: string;
};

type StaffingRequestTemplateType = {
  id: string;
  name: string;
};

type RoleType = {
  id: string;
  abbreviation: string;
};

type OrganizationType = {
  id: string;
  name: string;
};

type ClinicalSpecialtyType = {
  id: string;
  displayName: string;
};

type StaffingRequestSpecialtyType = {
  id: string;
  necessityLevel: string;
  clinicalSpecialty: ClinicalSpecialtyType;
};

type CertificationType = {
  id: string;
  credential: string;
};

type StaffingRequestCertificationType = {
  id: string;
  necessityLevel: string;
  certification: CertificationType;
};

type TechnologyType = {
  id: string;
  name: string;
};

type StaffingRequestTechnologyType = {
  id: string;
  technology: TechnologyType;
};

type WorksListTemplateType = {
  id: string;
  name: string;
};

type WorksMembershipType = {
  id: string;
  name: string;
};

type ApprovalChainApproverType = {
  id: string;
  worksMembership: WorksMembershipType;
};

type ApprovalChainApprovalType = {
  id: string;
  worksMembership: WorksMembershipType;
};

type ApprovalChainRejectionType = {
  id: string;
  worksMembership: WorksMembershipType;
};

type LastChangedValuesType = {
  shiftType?: ShiftType;
  hoursPerShift?: number;
  shiftsPerWeek?: number;
  startDate?: string;
  length?: number;
  billingRegularRate?: number;
  openStaffingRequestPositionsCount?: number;
  staffingRequestPositionsCount?: number;
  estimatedCost?: number;
  hiringLeader?: {
    id: string;
    name: string;
  };
  international?: boolean;
};

type ApprovalChainStepType = {
  id: string;
  satisfied: boolean;
  approvalChainApprovers: ApprovalChainApproverType[];
  approvalChainApprovals: ApprovalChainApprovalType[];
  approvalChainRejections: ApprovalChainRejectionType[];
};

type ApprovalChainType = {
  id: string;
  actionType: string;
  satisfied: boolean;
  rejected: boolean;
  createdAt: string;
  comments: CommentInfoFragmentType[];
  currentApprovers: ApprovalChainApproverType[];
  approvalChainSteps: ApprovalChainStepType[];
};

export type StaffingRequestDetailsDrawerType = {
  id: string;
  name: string;
  state: string;
  code: string;
  billingRegularRate: number;
  shiftType: ShiftType;
  hoursPerShift: number;
  shiftsPerWeek: number;
  length: number;
  startDate: string;
  international: boolean;
  openStaffingRequestPositionsCount: number;
  staffingRequestPositionsCount: number;
  estimatedCost: number;
  highestEstimatedCost: number;
  lowestEstimatedCost: number;
  roleLevel: RoleLevelEnum;
  organizationBusiness: OrganizationBusinessType;
  organizationUnit: OrganizationUnitType;
  staffingRequestTemplate: StaffingRequestTemplateType;
  role: RoleType;
  organization: OrganizationType;
  worksListTemplate: WorksListTemplateType;
  yearsOfExperienceHumanize?: string;
  externalId?: string;
  description?: string;
  lastChangedValues?: LastChangedValuesType;
  hiringLeader?: HiringLeaderType;
  history?: HistoryInfoFragmentType[];
  staffingRequestSpecialties?: StaffingRequestSpecialtyType[];
  staffingRequestCertifications?: StaffingRequestCertificationType[];
  staffingRequestTechnologies?: StaffingRequestTechnologyType[];
  billingCard?: BillingCardType;
  approvalChains?: ApprovalChainType[];
};

type StaffingRequestDetailsDrawerQueryResponse = {
  staffingRequest: StaffingRequestDetailsDrawerType;
};

type StaffingRequestDetailsDrawerQueryVariables = {
  id: string;
};

export const STAFFING_REQUEST_DETAILS_DRAWER_QUERY: TypedDocumentNode<
  StaffingRequestDetailsDrawerQueryResponse,
  StaffingRequestDetailsDrawerQueryVariables
> = gql`
  query StaffingRequestDetailsDrawerQuery($id: ID!) {
    staffingRequest(id: $id) {
      id
      name
      externalId
      description
      state
      code
      billingRegularRate
      shiftType
      hoursPerShift
      shiftsPerWeek
      length
      startDate
      international
      openStaffingRequestPositionsCount
      staffingRequestPositionsCount
      yearsOfExperienceHumanize
      estimatedCost
      highestEstimatedCost
      lowestEstimatedCost
      roleLevel
      lastChangedValues {
        shiftType
        hoursPerShift
        shiftsPerWeek
        startDate
        length
        billingRegularRate
        openStaffingRequestPositionsCount
        staffingRequestPositionsCount
        estimatedCost
        hiringLeader
        international
      }
      organizationBusiness {
        id
        name
      }
      organizationUnit {
        id
        name
        costCenter
        clinicalUnit {
          id
          displayName
        }
        department {
          id
          organizationBusiness {
            id
            name
          }
        }
      }
      hiringLeader {
        id
        name
      }
      staffingRequestTemplate {
        id
        name
      }
      role {
        id
        abbreviation
      }
      organization {
        id
        name
      }
      history {
        ...HistoryInfoFragment
      }
      organizationUnit {
        id
        costCenter
        clinicalUnit {
          id
          displayName
        }
        department {
          id
          organizationBusiness {
            id
            name
          }
        }
      }
      staffingRequestSpecialties {
        id
        necessityLevel
        clinicalSpecialty {
          id
          displayName
        }
      }
      staffingRequestCertifications {
        id
        necessityLevel
        certification {
          id
          credential
        }
      }
      staffingRequestTechnologies {
        id
        technology {
          id
          name
        }
      }
      billingCard {
        ...BillingCardFragment
      }
      worksListTemplate {
        id
        name
      }
      ...ApprovalChainStatusStaffingRequestFragment
    }
  }
  ${ApprovalChainStatusFragment.staffingRequest}
  ${HistoryInfoFragment}
  ${BillingCardFragment}
`;
